import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { RouterPaths } from 'src/components/Router/Router.constants';
import { SettingsTabs } from 'src/pages/Settings/Settings.enum';
import type { tAcceptanceSuccessModalProps } from './AcceptanceSuccessModal.types';

const NS = 'common/leadAgentAcceptanceModals';

/**
 * Lead Agent acceptance success modal (RET-1230 / C2, Unit 5). Confirms the
 * deferred acceptance with the chosen tier, renewal date and total. "Gestionar
 * suscripción" deep-links to the subscription tab with `cta_type` attribution;
 * "Entendido" closes onto a clean dashboard
 * (the banner already self-hid via `markResolved`).
 */
export default function AcceptanceSuccessModal(
  props: tAcceptanceSuccessModalProps,
) {
  const { tier, renewalTotal, chargeDate, onClose } = props;
  const { t } = useTranslation(NS);
  const navigate = useNavigate();

  function handleManageSubscription() {
    navigate(
      `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&cta_type=lead_agent_acceptance`,
    );
  }

  return (
    <Modal.Legacy
      open
      onClickBackdrop={onClose}
      className="LeadAgentAcceptanceSuccessModal rounded-lg p-0 min-w-[28rem] max-w-[28rem]"
    >
      <div className="flex justify-end p-2">
        <XMarkIcon
          onClick={onClose}
          className="size-6 cursor-pointer text-neutral-500"
          data-testid="acceptance-success-modal-close"
        />
      </div>

      <div className="px-6 pb-6 flex flex-col items-center text-center gap-4">
        <span className="flex items-center justify-center size-14 rounded-full bg-success/10 text-success">
          <CheckIcon className="size-7" aria-hidden="true" />
        </span>

        <h3 className="text-lg font-semibold">{t('success.title')}</h3>

        <div
          className="w-full rounded border border-base-300 p-3 flex flex-col gap-2 text-sm text-left"
          data-testid="acceptance-success-modal-detail"
        >
          <div className="flex justify-between">
            <span className="text-neutral-500">{t('success.plan')}</span>
            <span className="font-medium">{t(`tier.${tier}`)}</span>
          </div>
          {chargeDate ? (
            <div className="flex justify-between">
              <span className="text-neutral-500">{t('success.date')}</span>
              <span className="font-medium">{chargeDate}</span>
            </div>
          ) : null}
          {renewalTotal ? (
            <div className="flex justify-between">
              <span className="text-neutral-500">{t('success.total')}</span>
              <span
                className="font-medium"
                data-testid="acceptance-success-modal-total"
              >
                {renewalTotal}
              </span>
            </div>
          ) : null}
        </div>

        <div className="w-full flex flex-col gap-2">
          <Button
            color="primary"
            size="sm"
            className="rounded w-full"
            onClick={handleManageSubscription}
            data-testid="acceptance-success-modal-manage"
          >
            {t('success.manageSubscription')}
          </Button>
          <Button
            color="ghost"
            size="sm"
            className="rounded w-full"
            onClick={onClose}
            data-testid="acceptance-success-modal-dismiss"
          >
            {t('success.dismiss')}
          </Button>
        </div>
      </div>
    </Modal.Legacy>
  );
}
